import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarPlus, Check, ExternalLink, UserRound, Building2, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, notifyDeadlines } from "@/lib/api";
import { toast } from "sonner";

const URG = { alta: "bg-red-50 text-red-700", media: "bg-amber-50 text-amber-700", bassa: "bg-emerald-50 text-emerald-700" };

const parseAmount = (s) => {
  const n = parseFloat(String(s || "").replace(/[^\d,.]/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const AddDeadlineBtn = ({ s, r, profileId, i }) => {
  const [added, setAdded] = useState(false);
  const add = async () => {
    try {
      await api.post("/deadlines", { title: s.titolo, due_date: s.data, category: r.categoria || "Altro", profile_id: profileId || null, notes: r.riassunto, remind_days: r.documento_identita ? 90 : 7, amount: parseAmount(r.importo) });
      setAdded(true);
      notifyDeadlines();
      toast.success("Aggiunta allo Scadenzario");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Button size="sm" variant="outline" className="rounded-full" disabled={added} onClick={add} data-testid={`analysis-add-deadline-${i}`}>
      {added ? <Check className="h-4 w-4 text-emerald-600" /> : <CalendarPlus className="h-4 w-4" />}
    </Button>
  );
};

export const AnalysisResult = ({ analysis, demo }) => {
  const r = analysis.result || {};
  const valid = (r.scadenze || []).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s.data || ""));
  return (
    <div className="space-y-6 fade-up" data-testid="analysis-result">
      <div className="rounded-3xl border bg-white p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E07A5F]">{r.tipo_documento}</span>
          {r.urgenza && <span className={`text-xs rounded-full px-2.5 py-0.5 ${URG[r.urgenza] || URG.media}`} data-testid="analysis-urgency">Urgenza {r.urgenza}</span>}
          {analysis.doc_id && <span className="text-xs rounded-full bg-slate-100 px-2.5 py-0.5">Salvato in Archivio</span>}
        </div>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight" data-testid="analysis-title">{r.titolo}</h2>
        {r.mittente && <p className="text-sm text-slate-500">Da: {r.mittente}</p>}
        <p className="mt-4 text-slate-700 leading-relaxed" data-testid="analysis-summary">{r.riassunto}</p>
        {r.importo && <p className="mt-4 inline-block rounded-xl bg-[#1E3A8A]/5 px-4 py-2 font-display font-semibold text-[#1E3A8A]" data-testid="analysis-amount">Importo: {r.importo}</p>}
        {r.attenzione && (
          <div className="mt-4 flex gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"><AlertTriangle className="h-5 w-5 shrink-0" />{r.attenzione}</div>
        )}
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 rounded-3xl border bg-white p-6 md:p-8">
          <h3 className="font-medium text-lg">Cosa fare, passo passo</h3>
          <ol className="mt-4 space-y-3">
            {(r.cosa_fare || []).map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`analysis-step-${i}`}>
                <span className="h-6 w-6 shrink-0 rounded-full bg-[#1E3A8A] text-white text-xs grid place-items-center">{i + 1}</span>
                <span className="text-slate-700 leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
          {r.guida_correlata && (
            <Button asChild variant="outline" className="mt-6 rounded-full" data-testid="analysis-guide-link">
              <Link to={demo ? `/accedi?mode=register` : `/app/guide/${r.guida_correlata}`}><ListChecks className="h-4 w-4 mr-2" /> Apri la guida completa</Link>
            </Button>
          )}
        </div>
        <div className="md:col-span-2 space-y-6">
          {valid.length > 0 && (
            <div className="rounded-3xl border bg-white p-6">
              <h3 className="font-medium">Scadenze trovate</h3>
              {valid.map((s, i) => (
                <div key={i} className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3">
                  <div><p className="text-sm font-medium">{s.titolo}</p><p className="text-xs text-slate-500">{fmtDate(s.data)}</p></div>
                  {!demo && <AddDeadlineBtn s={s} r={r} profileId={analysis.profile_id} i={i} />}
                </div>
              ))}
            </div>
          )}
          <div className="rounded-3xl border bg-white p-6 space-y-4">
            <div><p className="flex items-center gap-2 font-medium"><UserRound className="h-4 w-4 text-[#1E3A8A]" /> Fai da te</p><p className="mt-1 text-sm text-slate-600 leading-relaxed">{r.fai_da_te}</p></div>
            <div><p className="flex items-center gap-2 font-medium"><Building2 className="h-4 w-4 text-[#E07A5F]" /> Quando serve il CAF</p><p className="mt-1 text-sm text-slate-600 leading-relaxed">{r.serve_caf}</p></div>
          </div>
          {(r.link_utili || []).length > 0 && (
            <div className="rounded-3xl border bg-white p-6">
              <h3 className="font-medium">Portali ufficiali</h3>
              {r.link_utili.filter((l) => l.url?.startsWith("https://")).map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-2 text-sm text-[#1E3A8A] hover:underline" data-testid={`analysis-link-${i}`}>
                  <ExternalLink className="h-3.5 w-3.5" />{l.nome}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 flex gap-2"><Check className="h-4 w-4" /> Analisi automatica a scopo orientativo: verifica sempre importi e scadenze sul documento originale.</p>
    </div>
  );
};
